import { Link } from "react-router-dom";
import { motion } from "framer-motion";

const CHARACTER_AVATAR =
  "https://customer-assets.emergentagent.com/job_minimalist-speed/artifacts/egen77ps_character%202.png";

const NAV_ITEMS = [
  { label: "About", to: "/about" },
  { label: "Wishlist", to: "https://throne.com/mishenkavt", external: true },
  { label: "Store", to: "/store" },
];

/**
 * Shared top navigation used by every page.
 * - Brand logo + name link to the home page ("/")
 * - "About" and "Store" links use client-side routing
 * - A 140x42 reserved slot on the right keeps room for the future "Watch Live" button
 */
export default function Nav({ activePath }) {
  return (
    <motion.nav
      initial={{ opacity: 0, y: -12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, ease: "easeOut" }}
      className="relative z-30 flex items-center justify-between px-5 py-5 md:px-12 md:py-6 lg:px-16"
      data-testid="site-nav"
    >
      <Link
        to="/"
        className="group flex items-center gap-3 outline-none"
        data-testid="brand-home-link"
        aria-label="Mishenka Iskandera — home"
      >
        <div
          className="flex h-10 w-10 items-center justify-center overflow-hidden rounded-full border border-white/60 bg-white/70 shadow-[0_4px_16px_rgba(244,63,94,0.12)] backdrop-blur-xl backdrop-saturate-150 transition-all duration-300 group-hover:-translate-y-0.5 group-hover:shadow-[0_6px_22px_rgba(244,63,94,0.22)]"
          data-testid="brand-avatar-circle"
        >
          <img
            src={CHARACTER_AVATAR}
            alt="Mishenka avatar"
            className="h-full w-full object-cover object-top scale-[1.45] translate-y-[6%]"
            data-testid="brand-avatar-image"
          />
        </div>
        <div className="leading-tight">
          <div className="font-display text-base font-semibold tracking-tight text-slate-900 transition-colors duration-300 group-hover:text-rose-500 md:text-lg">
            Mishenka Iskandera
          </div>
          <div className="text-[9px] uppercase tracking-[0.25em] text-slate-500 md:text-[10px]">
            VTuber · GT3 Racing
          </div>
        </div>
      </Link>

      <div className="hidden items-center gap-6 md:flex lg:gap-8">
        {NAV_ITEMS.map((item) => {
          const active = activePath === item.to;
          const baseClass = `group relative font-display text-sm font-medium transition-colors ${
            active ? "text-rose-500" : "text-slate-700 hover:text-rose-500"
          }`;
          const underline = (
            <span
              className={`absolute -bottom-1 left-0 h-px bg-rose-500 transition-all duration-300 ${
                active ? "w-full" : "w-0 group-hover:w-full"
              }`}
            />
          );

          if (item.external) {
            return (
              <a
                key={item.label}
                href={item.to}
                target="_blank"
                rel="noopener noreferrer"
                className={baseClass}
                data-testid={`nav-link-${item.label.toLowerCase()}`}
              >
                {item.label}
                {underline}
              </a>
            );
          }

          return (
            <Link
              key={item.label}
              to={item.to}
              className={baseClass}
              data-testid={`nav-link-${item.label.toLowerCase()}`}
            >
              {item.label}
              {underline}
            </Link>
          );
        })}
      </div>

      {/* Reserved space for the daily "Watch Live" CTA */}
      <div
        className="hidden h-[42px] w-[140px] md:block"
        aria-hidden="true"
        data-testid="watch-live-slot"
      />
    </motion.nav>
  );
}
